/**
 * Préparation du hors-ligne : les données des pages sont lues avec la connexion pour être gardées sur l'appareil.
 * - En arrière-plan (au plus une fois toutes les 30 minutes) : les pages principales ; pour un enseignant, les
 *   notes de ses matières de la période en cours.
 * - Complète (« Passer en mode hors ligne ») : en plus, l'accueil, les enseignants, le personnel et les notes de
 *   toutes les classes de la période en cours, avec l'avancement affiché.
 * Seul ce que le rôle permet de lire est chargé.
 */
import { getClasses, getSubjects } from "@/services/academicService";
import { listFeeItems } from "@/services/ficheService";
import {
  getAnnualResults,
  getClassReport,
  getEvaluationGrades,
  getEvaluations,
  getGradeScales,
  getPeriods,
  getTeachable,
} from "@/services/gradeService";
import { getStaff, getTeachers } from "@/services/hrService";
import { getBalances, getReceipts } from "@/services/paymentService";
import { getUnpaid } from "@/services/reminderService";
import { getByCycle, getOverview } from "@/services/reportService";
import { getActiveSchoolYear, getActivities, getCycles, getSchoolInfo, getSchoolYears } from "@/services/schoolService";
import { getEnrollments, getGuardians, getStudentDossier, getStudentGuardians, getStudents } from "@/services/studentService";
import { defaultPeriod } from "@/utils/period";
import { getOfflineStatus, setOfflineStatus } from "./status";

const EVERY_MS = 30 * 60 * 1000;
let lastRun = 0;
let running = false;

type Can = (permission: string) => boolean;

function currentPeriod<T extends { startDate: string; endDate: string }>(periods: T[]): T | undefined {
  const today = new Date().toISOString().slice(0, 10);
  return periods.find((p) => p.startDate <= today && today <= p.endDate) ?? [...periods].reverse().find((p) => p.startDate <= today) ?? periods[0];
}

/** Compte les lectures (réussies ou non) pour l'avancement de la préparation complète. */
class Tracker {
  done = 0;
  total = 0;
  failed = 0;
  private readonly visible: boolean;
  constructor(visible: boolean) {
    this.visible = visible;
  }

  /** Lance les lectures, 6 à la fois au plus (une connexion lente n'est pas saturée). */
  async run(tasks: Array<() => Promise<unknown>>): Promise<void> {
    this.total += tasks.length;
    this.show();
    const queue = [...tasks];
    const worker = async () => {
      for (let task = queue.shift(); task; task = queue.shift()) {
        try {
          await task();
        } catch {
          this.failed += 1;
        }
        this.done += 1;
        this.show();
      }
    };
    await Promise.all(Array.from({ length: Math.min(6, queue.length) }, worker));
  }

  /** Étape affichée sous la barre (« Page Élèves »…). */
  step(label: string): void {
    if (this.visible) setOfflineStatus({ preparing: { done: this.done, total: this.total, label } });
  }

  /** Une étape faite hors des lectures comptées (une page parcourue). */
  count(total: number, done: number): void {
    this.total += total;
    this.done += done;
    this.show();
  }

  private show(): void {
    if (this.visible) setOfflineStatus({ preparing: { done: this.done, total: this.total, label: getOfflineStatus().preparing?.label } });
  }
}

/** Préparation en arrière-plan, si elle n'a pas eu lieu depuis 30 minutes (silencieuse). */
export async function prefetchForOffline(can: Can): Promise<void> {
  const { online, forced } = getOfflineStatus();
  if (running || Date.now() - lastRun < EVERY_MS || !online || forced) return;
  running = true;
  lastRun = Date.now();
  try {
    await prepare(can, new Tracker(false), false);
  } finally {
    running = false;
  }
}

/**
 * Préparation complète avant de passer en mode hors ligne : les données, puis `visitPages` (le parcours de
 * toutes les pages et de leurs onglets, fourni par l'écran). Renvoie le nombre de lectures et d'échecs.
 */
export async function prepareEverything(
  can: Can,
  visitPages: (tracker: Tracker) => Promise<void>,
): Promise<{ total: number; failed: number }> {
  const tracker = new Tracker(true);
  running = true;
  try {
    tracker.step("Données principales");
    await prepare(can, tracker, true);
    await visitPages(tracker);
    lastRun = Date.now();
    return { total: tracker.total, failed: tracker.failed };
  } finally {
    running = false;
    setOfflineStatus({ preparing: null });
  }
}

export type { Tracker };

async function prepare(can: Can, tracker: Tracker, full: boolean): Promise<void> {
  const years = await getSchoolYears().catch(() => []);
  const year = getActiveSchoolYear();
  const tasks: Array<() => Promise<unknown>> = [getCycles, getSchoolInfo];
  if (can("classes:read")) tasks.push(getClasses);
  if (can("subjects:read")) tasks.push(getSubjects);
  if (can("students:read")) tasks.push(getStudents);
  if (can("guardians:read")) tasks.push(getGuardians, getStudentGuardians);
  if (can("enrollments:read")) tasks.push(getEnrollments);
  if (can("payments:read")) tasks.push(getReceipts, ...years.map((y) => () => getBalances(y.label)));
  if (can("fees:read") && year) tasks.push(() => listFeeItems(year));
  if (can("reminders:read") && year) {
    tasks.push(() => getUnpaid({ schoolYear: year, mode: "overdue" }), () => getUnpaid({ schoolYear: year, mode: "unpaid" }));
  }
  if (full) {
    // Accueil du tableau de bord et ressources humaines.
    tasks.push(getActivities);
    if (can("teachers:read")) tasks.push(getTeachers);
    if (can("staff:read")) tasks.push(getStaff);
    if (can("reports:read")) tasks.push(() => getOverview(defaultPeriod()), () => getByCycle(defaultPeriod()));
  }
  await tracker.run(tasks);
  if (can("grades:read") && year) await prepareGrades(year, tracker, full);
  if (!full) return;
  // Ce qui ne s'ouvre qu'au clic : le dossier de chaque élève, les bulletins de chaque classe.
  if (can("students:read")) {
    tracker.step("Dossiers des élèves");
    const students = await getStudents().catch(() => []);
    await tracker.run(students.map((s) => () => getStudentDossier(s.id)));
  }
  if (can("report-cards:read") && year) {
    tracker.step("Bulletins des classes");
    const [periods, classes] = await Promise.all([getPeriods(year).catch(() => []), getClasses().catch(() => [])]);
    await tracker.run(
      classes.flatMap((c) => [
        ...periods.map((p) => () => getClassReport(p.id, c.id)),
        () => getAnnualResults(c.id, year),
      ]),
    );
  }
}

/** Évaluations de la période en cours et leurs notes (un enseignant : ses matières ; préparation complète : toutes). */
async function prepareGrades(year: string, tracker: Tracker, full: boolean): Promise<void> {
  const [periods, teachable] = await Promise.all([getPeriods(year), getTeachable(), getGradeScales()]).catch((): [Awaited<ReturnType<typeof getPeriods>>, null] => [[], null]);
  const period = currentPeriod(periods);
  if (!period || !teachable) return;
  const classIds = teachable.all ? (await getClasses().catch(() => [])).map((c) => c.id) : teachable.assignments.map((a) => a.classId);
  const evaluationsByClass = new Map<string, Awaited<ReturnType<typeof getEvaluations>>>();
  await tracker.run(
    classIds.map((classId) => async () => {
      evaluationsByClass.set(classId, await getEvaluations(period.id, classId));
    }),
  );
  // Direction en arrière-plan : trop de matières pour tout garder (les notes ouvertes restent gardées).
  if (teachable.all && !full) return;
  const gradeTasks: Array<() => Promise<unknown>> = [];
  for (const classId of classIds) {
    const assignment = teachable.assignments.find((a) => a.classId === classId);
    for (const evaluation of evaluationsByClass.get(classId) ?? []) {
      const subjectIds = teachable.all || assignment?.allSubjects ? evaluation.subjects.map((s) => s.subjectId) : (assignment?.subjectIds ?? []);
      for (const subjectId of subjectIds) gradeTasks.push(() => getEvaluationGrades(evaluation.id, subjectId));
    }
  }
  await tracker.run(gradeTasks);
}
